import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormikContextType } from 'formik';

import MCard from 'app/ui/MCard/MCard';
import MIcon from 'app/ui/MIcon/MIcon';

import setFieldProps from 'app/utils/setFieldProps';
import MFormInput from 'app/ui/MFormInput/MFormInput';
import MButton from 'app/ui/MButton/MButton';

import routeMap from '../../../assets/images/routeMap.png';
import googleLogo from '../../../assets/images/googlelogo.png';

import { CreateAccountState, ISignInFields, ISignUpFields } from './CreateAccount.model';

import classes from './CreateAccount.module.scss';

interface Props {
  cardRef?: React.RefObject<HTMLDivElement>;
  account: CreateAccountState;
  errorSignInMessage: string | null;
  errorSignUpMessage: string | null;
  formSignIn: FormikContextType<ISignInFields>;
  formSignUp: FormikContextType<ISignUpFields>;
  onIconClick: () => void;
  onLogin: () => void;
  onJoinUs: () => void;
  onSignUp: () => void;
  onContainerClick: (event: React.MouseEvent) => void;
}

const CreateAccountView: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  return (
    <div className={classes['container']} onClick={props.onContainerClick}>
      <MCard className={classes['content']}>
        <MIcon className={classes['btnClose']} name="cisX" onClick={props.onIconClick}></MIcon>
        <header>
          {props.account === CreateAccountState.Default ? (
            <React.Fragment>
              <span className={classes['title']}>{t('createAccount.defailt.title')}</span>
              <span className={classes['subTitle']}>{t('createAccount.defailt.subTitle')}</span>
              <div className={classes['image']}>
                <img src={routeMap} />
              </div>
            </React.Fragment>
          ) : props.account === CreateAccountState.SignIn ? (
            <React.Fragment>
              <span className={classes['title']}>{t('createAccount.signIn.title')}</span>
              <span className={classes['subTitle']}>{t('createAccount.signIn.subTitle')}</span>
            </React.Fragment>
          ) : (
            <span className={classes['title']}>{t('createAccount.signUp.title')}</span>
          )}
        </header>
        <div className={classes['actions']}>
          {props.account === CreateAccountState.Default ? (
            <React.Fragment>
              <MButton type="button" className={classes['btnGoogle']}>
                <img className={classes['btnGoogle__logo']} src={googleLogo} />
                {t('createAccount.signUp.btnGoogle')}
              </MButton>
              <div className={classes['separateOr']}>
                <hr className={classes['separateOr__line']}></hr>
                <span className={classes['separateOr__text']}>{t('createAccount.signUp.separateOr')}</span>
              </div>
            </React.Fragment>
          ) : props.account === CreateAccountState.SignUp ? (
            <React.Fragment>
              <MFormInput
                className={classes['signUpFields']}
                {...setFieldProps(props.formSignUp, 'firstName')}
                label={t('createAccount.signUp.form.fields.firstName.label')}
              ></MFormInput>
              <MFormInput
                className={classes['signUpFields']}
                {...setFieldProps(props.formSignUp, 'lastName')}
                label={t('createAccount.signUp.form.fields.lastName.label')}
              ></MFormInput>
              <MFormInput
                className={classes['signUpFields']}
                {...setFieldProps(props.formSignUp, 'phone')}
                label={t('createAccount.signUp.form.fields.phone.label')}
              ></MFormInput>
              <MFormInput
                className={classes['signUpFields']}
                {...setFieldProps(props.formSignUp, 'email')}
                label={t('createAccount.signUp.form.fields.email.label')}
              ></MFormInput>
              <MFormInput
                className={classes['signUpFields']}
                {...setFieldProps(props.formSignUp, 'password')}
                label={t('createAccount.signUp.form.fields.password.label')}
              ></MFormInput>
              <MButton type="button" className={classes['btnSignIn']} onClick={props.formSignUp.submitForm}>
                {t('createAccount.signUp.btnSignUp')}
              </MButton>
              {props.errorSignUpMessage ? <span className={classes['errorMessage']}>{props.errorSignUpMessage}</span> : null}
            </React.Fragment>
          ) : null}

          {props.account === CreateAccountState.Default ? (
            <MButton type="button" className={classes['btnSignIn']} onClick={props.onSignUp}>
              {t('createAccount.defailt.btnSignUp')}
            </MButton>
          ) : props.account === CreateAccountState.SignIn ? (
            <div className={classes['signInForm']}>
              <MFormInput
                {...setFieldProps(props.formSignIn, 'email')}
                label={t('createAccount.signIn.form.fields.email.label')}
              ></MFormInput>
              <MFormInput
                inputType="password"
                {...setFieldProps(props.formSignIn, 'password')}
                label={t('createAccount.signIn.form.fields.password.label')}
              ></MFormInput>
              <MButton type="button" className={classes['btnSignIn']} onClick={props.formSignIn.submitForm}>
                {t('createAccount.signIn.btnSignIn')}
              </MButton>
              {props.errorSignInMessage ? <span className={classes['errorMessage']}>{props.errorSignInMessage}</span> : null}
            </div>
          ) : null}

          <div className={classes['logIn']}>
            {t('createAccount.defailt.alreadyHave')}
            {props.account === CreateAccountState.Default || props.account === CreateAccountState.SignUp ? (
              <span onClick={props.onLogin} className={classes['logIn__action']}>{t('createAccount.defailt.logIn')}</span>
            ) : (
              <span onClick={props.onJoinUs} className={classes['logIn__action']}>{t('createAccount.defailt.joinUs')}</span>
            )}
          </div>
        </div>
        <footer className={classes['footer']}>
          <span className={classes['text']}>
            {t('createAccount.footer.text')} <a className={classes['text__link']} href=""> {t('createAccount.footer.termsOfUse')}</a> {t('createAccount.footer.and')}{' '}
            <a className={classes['text__link']} href=""> {t('createAccount.footer.privacyPolicy')}</a>
          </span>
        </footer>
      </MCard>
    </div>
  );
};

CreateAccountView.displayName = 'CreateAccountView';
CreateAccountView.defaultProps = {};

export default CreateAccountView;
